import { FilterTrigger } from "@/components/filter/filter-trigger";
import { getComponentFromType } from "@/components/relation-display-components/view-component-renderer";
import { FormField } from "@/hooks/schema/use-schema";
import { useEntityViewComponents } from "@/hooks/use-entity-view-components";
import {
  ComponentTemplateTypes,
  EntityViewComponent,
  EntityViewComponentMapping,
  toComponentTemplates,
} from "@/services/entity-view-components.service";
import { VariableDef } from "@/components/variable-input";
import { VariableInputEditor } from "@/components/variable-input-editor/variable-input-editor";
import { entityFieldsAsVariables } from "@/lib/variable-type-utils";
import { EntitySchema } from "@/services/entity-instance.service";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from "@repo/ui";
import { CheckCircle, ChevronDown, ChevronLeft, Code2 } from "lucide-react";
import { useMemo, useState } from "react";

interface RelationSelectionComponentProps {
  onSelect: (component?: EntityViewComponentMapping) => void;
  entityName: string;
  mapping?: EntityViewComponentMapping;
  /** fields for single field */
  relation_field_to_display?: string;
  targetEntitySchema: EntitySchema;
  handleRelationFieldSelect: (selectedField: FormField) => void;
  /*** end fields for single field */
}
export const EntityViewComponentSelection: React.FC<RelationSelectionComponentProps> = ({
  onSelect,
  entityName,
  mapping,
  relation_field_to_display,
  targetEntitySchema,
  handleRelationFieldSelect,
}) => {
  const { components } = useEntityViewComponents();
  const [searchValue, setSearchValue] = useState("");
  const filteredComponents = useMemo(() => {
    return components.filter((x) =>
      x.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
    );
  }, [components, searchValue]);
  const [selectedComponent, setSelectedComponent] = useState<EntityViewComponent>();
  const [showMapping, setShowMapping] = useState(false);
  const handleSelectComponent = (component: EntityViewComponent) => {
    setSelectedComponent(component);
    setShowMapping(true);
  };
  const handleBack = () => {
    setShowMapping(false);
  };
  const handleOpenChange = (open: boolean) => {
    if (!open) return;
    const current = components.find((c) => c.type === mapping?.component);
    setSelectedComponent(current);
    setShowMapping(!!current);
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger>
        {mapping?.component ?? relation_field_to_display ?? "select a component"}
      </DialogTrigger>
      <DialogContent className="max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select View Component</DialogTitle>
          <DialogDescription>
            Choose how to display <span className="font-bold">{entityName}</span> in your view.
          </DialogDescription>
        </DialogHeader>
        {!showMapping || !selectedComponent ? (
          <div>
            <Label>Search</Label>
            <Input
              placeholder="Search for components"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <div className="grid gap-2 overflow-y-auto pt-3">
              <Card
                key={"single_field"}
                onClick={() =>
                  handleSelectComponent({
                    type: "SINGLE_FIELD" as unknown as ComponentTemplateTypes,
                    default_opts: {},
                    fields: [],
                    name: "Single field",
                  })
                }
                className="cursor-pointer hover:bg-slate-50"
              >
                <CardHeader>
                  <CardTitle>{"Single field"}</CardTitle>
                  <CardDescription>{"A single field displayed"}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg bg-slate-100 px-4 py-2">
                    <p>Name</p>
                  </div>
                </CardContent>
              </Card>
              {filteredComponents?.map((c, idx) => {
                const Component = getComponentFromType(c.type);
                if (!Component) return null;
                return (
                  <Card
                    key={idx}
                    onClick={() => handleSelectComponent(c)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    {selectedComponent?.type === c.type && (
                      <div className="relative">
                        <div className="absolute right-2 top-2">
                          <CheckCircle size={"1rem"} />
                        </div>
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle>{c.name}</CardTitle>
                      <CardDescription>{c.description}</CardDescription>
                    </CardHeader>

                    <CardContent>
                      <div className="rounded-lg bg-slate-100">
                        <Component config={c.default_opts} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {(selectedComponent.type as unknown) == "SINGLE_FIELD" ? (
              <div className="flex flex-col gap-4">
                <div>
                  <span className="font-bold">Field: </span>
                  <FilterTrigger
                    entitySchema={targetEntitySchema as unknown as EntitySchema}
                    onSelect={handleRelationFieldSelect}
                    fieldsToHide={["created_at", "updated_at", "integer_id"]}
                    amountLevels={1}
                    hideListRelations={true}
                  >
                    <div className="inline-flex h-8 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-2 font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
                      {relation_field_to_display || "Select field"}
                      <ChevronDown className="ml-1 h-3 w-3" />
                    </div>
                  </FilterTrigger>
                </div>

                <DialogFooter onClick={handleBack}>
                  <Button variant={"outline"}>
                    <ChevronLeft />
                    Back
                  </Button>
                  <DialogClose asChild>
                    <Button className="grow" onClick={() => onSelect(undefined)}>
                      Ok
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </div>
            ) : (
              <ComponentFieldMapping
                targetEntitySchema={targetEntitySchema}
                selectedComponent={selectedComponent}
                key={selectedComponent.type}
                mappingConfig={mapping?.config as string}
                setMappingConfig={(config) =>
                  onSelect({
                    ...mapping,
                    component: selectedComponent.type,
                    config: config,
                  })
                }
                onBack={handleBack}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface ComponentFieldMappingProps {
  selectedComponent: EntityViewComponent;
  mappingConfig?: string;
  setMappingConfig: (config: string) => void;
  targetEntitySchema: EntitySchema;
  onBack: () => void;
}
const ComponentFieldMapping: React.FC<ComponentFieldMappingProps> = ({
  selectedComponent,
  mappingConfig,
  targetEntitySchema,
  setMappingConfig,
  onBack,
}) => {
  const [templates, setTemplates] = useState(() => toComponentTemplates(mappingConfig));
  const Render = getComponentFromType(selectedComponent.type)!;
  const variables = useMemo(
    () => entityFieldsAsVariables(targetEntitySchema.fields),
    [targetEntitySchema],
  );
  const hasTemplates = Object.values(templates).some(Boolean);

  return (
    <div className="gap flex flex-col gap-4">
      <div className="rounded-lg bg-slate-100">
        <Render config={hasTemplates ? templates : selectedComponent.default_opts} />
      </div>
      <p className="text-sm text-muted-foreground">
        Type text and insert fields with @. For a fallback, edit the raw text and write{" "}
        <code>{"${nickname ?? name}"}</code>.
      </p>

      {selectedComponent.fields.map((c) => (
        <TemplateField
          key={c.field_name}
          fieldName={c.field_name}
          required={c.required}
          value={templates[c.field_name] ?? ""}
          variables={variables}
          onChange={(template) => setTemplates((prev) => ({ ...prev, [c.field_name]: template }))}
        />
      ))}

      <div className="border-b"></div>
      <DialogFooter onClick={onBack}>
        <Button variant={"outline"}>
          <ChevronLeft />
          Back
        </Button>
        <DialogClose asChild>
          <Button className="grow" onClick={() => setMappingConfig(JSON.stringify(templates))}>
            Save
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
};

interface TemplateFieldProps {
  fieldName: string;
  required?: boolean;
  value: string;
  variables: VariableDef[];
  onChange: (value: string) => void;
}
const TemplateField: React.FC<TemplateFieldProps> = ({
  fieldName,
  required,
  value,
  variables,
  onChange,
}) => {
  const [raw, setRaw] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label>
          {fieldName} {required && <span className="text-red-600">*</span>}
        </Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 text-xs text-muted-foreground"
          onClick={() => setRaw(!raw)}
        >
          <Code2 className="mr-1 h-3.5 w-3.5" />
          {raw ? "Back to editor" : "Edit raw"}
        </Button>
      </div>
      {raw ? (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-xs"
        />
      ) : (
        <VariableInputEditor
          value={value}
          onChange={onChange}
          variables={variables}
          multiline={false}
          inModal
          placeholder="Type text, @ inserts a field"
          className="rounded-md border border-input px-3 py-2"
        />
      )}
    </div>
  );
};
